// Personnel sync check: the roster against the MailChimp audience and the Entra
// tenant (GET /personnel-sync/, rules in backend core/personnel_sync.py). Each
// discrepancy can be fixed from its row; the additive fixes can also be run over
// every row shown. Reached from a button on the Personnel page; Super Admin only.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, LinearProgress, Link, ListItemText,
  Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import BuildIcon from "@mui/icons-material/Build";
import { API_BASE_URL } from "../config";
import { PERSONNEL_STATUSES } from "../constants";
import PersonnelDialog from "../components/PersonnelDialog";
import { exportPersonnelSync } from "../exportExcel";
import { notify } from "../notify";
import {
  FIXES, ISSUES, fixFor, fixesFor, filterRows, issueLabel, mailchimpText, o365Text,
} from "../personnelSync";

const API = `${API_BASE_URL}/personnel-sync`;
const PERSONNEL_API = `${API_BASE_URL}/personnel`;
const NO_STATUS = "(none)";

// One fix, against the server. Returns nothing; throws on failure.
const runFix = (fix, row, { quiet = false, sendInvite = false } = {}) => {
  const opts = quiet ? { suppressErrorToast: true } : undefined;
  if (fix === "o365_grant") {
    return axios.post(`${PERSONNEL_API}/${row.personnel_id}/tenant-access`,
                      { send_invitation_email: sendInvite }, opts);
  }
  const action = { mc_add: "add", mc_sync: "sync", mc_archive: "archive" }[fix];
  return row.personnel_id
    ? axios.post(`${API}/${row.personnel_id}/mailchimp`, { action }, opts)
    : axios.post(`${API}/mailchimp-archive`, { email: row.email }, opts);
};

const errorText = (err) => err.response?.data?.detail || err.message || "Failed";

const Tick = ({ on }) => (on
  ? <CheckIcon fontSize="inherit" color="success" sx={{ verticalAlign: "middle" }} />
  : <CloseIcon fontSize="inherit" color="disabled" sx={{ verticalAlign: "middle" }} />);

// "a, b" with any value the other side lacks in bold.
const Compared = ({ label, mine, theirs }) => (
  <Typography variant="caption" component="div">
    <Box component="span" sx={{ color: "text.secondary" }}>{label}: </Box>
    {mine.length === 0 ? "—" : mine.map((v, i) => (
      <Box component="span" key={v} sx={{ fontWeight: theirs && !theirs.includes(v) ? 700 : 400 }}>
        {i > 0 && ", "}{v}
      </Box>
    ))}
  </Typography>
);

export default function PersonnelSync() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issueFilter, setIssueFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [text, setText] = useState("");
  const [showAll, setShowAll] = useState(false);
  // Fixes made since the last check, by row key, so a fixed issue reads as done
  // without re-reading both services after every click.
  const [done, setDone] = useState({});
  const [busy, setBusy] = useState({});     // row key -> fix running
  const [errors, setErrors] = useState({}); // row key -> last fix's error
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null); // { fix, row } for a single archive
  const [bulkMenu, setBulkMenu] = useState(null);
  const [bulk, setBulk] = useState(null); // { fix, rows, sendInvite, running, index, failures, finished }
  const stopBulk = useRef(false);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/`)
      .then(res => { setData(res.data); setDone({}); setErrors({}); })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const rows = data?.rows || [];
  const filtered = filterRows(rows, {
    issues: issueFilter, statuses: statusFilter, text, withIssuesOnly: !showAll,
  });
  const isDone = (row, fix) => (done[row.key] || []).includes(fix);
  const openFixes = (row) => fixesFor(row).filter(f => !isDone(row, f));

  const markDone = (row, fix) =>
    setDone(d => ({ ...d, [row.key]: [...(d[row.key] || []), fix] }));

  const fixOne = (fix, row) => {
    setBusy(b => ({ ...b, [row.key]: fix }));
    setErrors(e => ({ ...e, [row.key]: null }));
    runFix(fix, row, { quiet: true })
      .then(() => { markDone(row, fix); notify(`${FIXES[fix].label}: done for ${row.name || row.email}.`, "success"); })
      .catch(err => setErrors(e => ({ ...e, [row.key]: errorText(err) })))
      .finally(() => setBusy(b => ({ ...b, [row.key]: null })));
  };

  // --- Bulk: one fix over every row shown that still needs it ---
  const bulkRows = (fix) => filtered.filter(r => openFixes(r).includes(fix));
  const startBulk = (fix) => {
    setBulkMenu(null);
    setBulk({ fix, rows: bulkRows(fix), sendInvite: false, running: false, index: 0,
              failures: [], finished: false });
  };
  const runBulk = async () => {
    const { fix, rows: targets, sendInvite } = bulk;
    stopBulk.current = false;
    setBulk(b => ({ ...b, running: true }));
    const failures = [];
    let i = 0;
    // Stop takes effect before the next call, never in the middle of one.
    for (; i < targets.length && !stopBulk.current; i++) {
      const index = i;
      setBulk(b => ({ ...b, index }));
      const row = targets[index];
      try {
        await runFix(fix, row, { quiet: true, sendInvite });
        markDone(row, fix);
      } catch (err) {
        failures.push({ row, error: errorText(err) });
        setErrors(e => ({ ...e, [row.key]: errorText(err) }));
      }
    }
    setBulk(b => ({ ...b, running: false, finished: true, index: i, failures,
                    stopped: i < targets.length }));
  };

  // --- Filters ---
  const issueOptions = Object.keys(ISSUES).filter(c => rows.some(r => r.issues.some(i => i.code === c)));
  const statusOptions = [...PERSONNEL_STATUSES.map(s => s.value), NO_STATUS];
  const multiSelect = (label, value, setValue, options, render = v => v) => (
    <TextField select size="small" label={label} sx={{ minWidth: 200 }}
               value={value} onChange={e => setValue(e.target.value)}
               slotProps={{ select: { multiple: true, renderValue: sel => sel.map(render).join(", ") } }}>
      {options.map(o => (
        <MenuItem key={o} value={o}>
          <Checkbox size="small" checked={value.includes(o)} />
          <ListItemText primary={render(o)} />
        </MenuItem>
      ))}
    </TextField>
  );

  const sourceLine = (name, s, countText) => {
    if (!s) return null;
    if (!s.configured) return <Alert severity="info">{name} is not configured on the server, so it isn't checked.</Alert>;
    if (s.error) return <Alert severity="error">{name} couldn't be read, so it isn't checked: {s.error}</Alert>;
    return <Typography variant="body2" color="text.secondary">{countText}</Typography>;
  };

  const bulkCounts = Object.keys(FIXES).filter(f => FIXES[f].bulk)
    .map(f => ({ fix: f, n: bulkRows(f).length }));

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }} useFlexGap>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/personnel")}>Personnel</Button>
        <Typography variant="h4" sx={{ flex: 1 }}>MailChimp &amp; O365 check</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Re-check</Button>
        <Button variant="outlined" startIcon={<BuildIcon />} disabled={loading || filtered.length === 0}
                onClick={e => setBulkMenu(e.currentTarget)}>
          Fix all shown
        </Button>
        <Button variant="outlined" startIcon={<FileDownloadIcon />} disabled={filtered.length === 0}
                onClick={() => exportPersonnelSync(filtered)}>
          Export to Excel
        </Button>
      </Stack>
      <Menu anchorEl={bulkMenu} open={!!bulkMenu} onClose={() => setBulkMenu(null)}>
        {bulkCounts.map(({ fix, n }) => (
          <MenuItem key={fix} disabled={n === 0} onClick={() => startBulk(fix)}>
            {FIXES[fix].label} ({n})
          </MenuItem>
        ))}
      </Menu>

      <Typography variant="caption" color="text.secondary">
        Expected: Clear to Work, Onboard Complete and Onboarding in both; Race Organiser in O365 only; anyone whose
        only Shelter Stone roles are Mountain Safety / Mountain Safety Plus, and every other
        status, in neither; nobody in either without a record. MailChimp is matched on the
        contact email, O365 on the sign-in address. Those three also need a role
        and a qualification. Removing O365 access is done in Entra — use the row's link.
      </Typography>
      {data && (
        <Stack spacing={0.5}>
          {sourceLine("MailChimp", data.mailchimp, `MailChimp: ${data.mailchimp.count} subscribed contacts.`)}
          {sourceLine("O365", data.o365, (
            <>O365: {data.o365.count} users in the group {data.o365.group_name ? `“${data.o365.group_name}”` : ""}
              {data.o365.group_url && (
                <> — <Link href={data.o365.group_url} target="_blank" rel="noopener noreferrer">open in Entra</Link></>
              )}.</>
          ))}
        </Stack>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <TextField size="small" label="Search name, email, notes" value={text} onChange={e => setText(e.target.value)} />
        {multiSelect("Discrepancy", issueFilter, setIssueFilter, issueOptions, issueLabel)}
        {multiSelect("Status", statusFilter, setStatusFilter, statusOptions,
                     v => (v === NO_STATUS ? "Not in database" : v))}
        <FormControlLabel control={<Checkbox size="small" checked={showAll} onChange={e => setShowAll(e.target.checked)} />}
                          label="Include rows with no discrepancy" />
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
          {loading ? "Checking…" : `${filtered.length} of ${rows.length}`}
        </Typography>
      </Stack>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>Person</TableCell>
                <TableCell>Should be in</TableCell>
                <TableCell>MailChimp</TableCell>
                <TableCell>O365</TableCell>
                <TableCell>Roles &amp; qualifications</TableCell>
                <TableCell>Discrepancies</TableCell>
                <TableCell>Private notes</TableCell>
                <TableCell>Fix</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(r => (
                <TableRow key={r.key} sx={{ verticalAlign: "top" }}>
                  <TableCell>
                    {r.personnel_id
                      ? <Link component="button" variant="body2" onClick={() => setEditing(r.personnel_id)}>{r.name}</Link>
                      : <Typography variant="body2">{r.name || "—"}</Typography>}
                    <Typography variant="caption" component="div" color="text.secondary">{r.email || "no email"}</Typography>
                    {r.login_email && (
                      <Typography variant="caption" component="div" color="text.secondary">signs in as {r.login_email}</Typography>
                    )}
                    <Typography variant="caption" component="div">{r.status || "Not in database"}</Typography>
                    {r.related && (
                      <Typography variant="caption" component="div">
                        An address on <Link component="button" variant="caption"
                                            onClick={() => setEditing(r.related.personnel_id)}>{r.related.name}</Link>'s record
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {r.expected ? (
                      <>
                        <Typography variant="body2">MailChimp <Tick on={r.expected.mailchimp} /></Typography>
                        <Typography variant="body2">O365 <Tick on={r.expected.o365} /></Typography>
                        <Typography variant="caption" color="text.secondary">{r.expected.reason}</Typography>
                      </>
                    ) : <Typography variant="body2">Neither</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{mailchimpText(r)}</Typography>
                    {r.mailchimp?.admin_url && (
                      <Link href={r.mailchimp.admin_url} target="_blank" rel="noopener noreferrer" variant="caption">
                        Open <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: "middle" }} />
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{o365Text(r)}</Typography>
                    {r.o365 && (
                      <Typography variant="caption" component="div">
                        Web app group <Tick on={r.o365.in_group} />
                      </Typography>
                    )}
                    {r.o365?.portal_url && (
                      <Link href={r.o365.portal_url} target="_blank" rel="noopener noreferrer" variant="caption">
                        Open in Entra <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: "middle" }} />
                      </Link>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    {r.personnel_id && (
                      <>
                        <Compared label="Roles" mine={r.roles} theirs={r.mailchimp?.roles} />
                        <Compared label="Quals" mine={r.qualifications} theirs={r.mailchimp?.qualifications} />
                      </>
                    )}
                    {r.mailchimp && (
                      <>
                        <Compared label="MC roles" mine={r.mailchimp.roles} theirs={r.personnel_id ? r.roles : null} />
                        <Compared label="MC quals" mine={r.mailchimp.qualifications}
                                  theirs={r.personnel_id ? r.qualifications : null} />
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
                      {r.issues.map(i => {
                        const fixed = isDone(r, fixFor(i.code, r));
                        return (
                          <Tooltip key={i.code} title={fixed ? "Fixed since the last check" : i.text}>
                            <Chip size="small" label={issueLabel(i.code)}
                                  color={fixed ? "success" : ISSUES[i.code]?.side === "db" ? "warning" : "error"}
                                  variant={fixed ? "filled" : "outlined"}
                                  icon={fixed ? <CheckIcon /> : undefined}
                                  sx={fixed ? { textDecoration: "line-through" } : undefined} />
                          </Tooltip>
                        );
                      })}
                      {r.issues.length === 0 && "—"}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260, whiteSpace: "pre-wrap" }}>
                    <Typography variant="caption">{r.private_notes || ""}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
                      {openFixes(r).map(f => (
                        <Button key={f} size="small" variant="outlined" disabled={!!busy[r.key]}
                                startIcon={busy[r.key] === f ? <CircularProgress size={14} /> : null}
                                onClick={() => (f === "mc_archive" ? setConfirm({ fix: f, row: r }) : fixOne(f, r))}
                                sx={{ whiteSpace: "nowrap" }}>
                          {FIXES[f].label}
                        </Button>
                      ))}
                      {errors[r.key] && <Typography variant="caption" color="error">{errors[r.key]}</Typography>}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: "text.secondary", py: 4 }}>
                    {loading ? <CircularProgress size={28} />
                      : rows.length === 0 ? "Nothing to compare." : "No discrepancies match your filter."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Archiving one contact */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>Archive in MailChimp?</DialogTitle>
        <DialogContent>
          <Typography>
            {confirm?.row.email} will be removed from the MailChimp audience. MailChimp keeps
            their history, and they can be added back later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained"
                  onClick={() => { fixOne(confirm.fix, confirm.row); setConfirm(null); }}>
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk fix */}
      <Dialog open={!!bulk} maxWidth="sm" fullWidth
              onClose={() => { if (!bulk?.running) setBulk(null); }}>
        <DialogTitle>{bulk && FIXES[bulk.fix].label} — {bulk?.rows.length} people</DialogTitle>
        <DialogContent>
          {bulk && !bulk.running && !bulk.finished && (
            <Stack spacing={1}>
              <Typography>
                This runs over every row currently shown that needs it, one at a time. It's safe
                to stop and re-run: anyone already done is skipped.
              </Typography>
              <Typography variant="body2" component="div" sx={{ maxHeight: 200, overflow: "auto" }}>
                {bulk.rows.map(r => r.name || r.email).join(", ")}
              </Typography>
              {bulk.fix === "o365_grant" && (
                <FormControlLabel
                  control={<Checkbox checked={bulk.sendInvite}
                                     onChange={e => setBulk(b => ({ ...b, sendInvite: e.target.checked }))} />}
                  label="Send Microsoft's invitation email to anyone newly invited (otherwise tell them yourself — access works on first sign-in either way)" />
              )}
            </Stack>
          )}
          {bulk && (bulk.running || bulk.finished) && (
            <Stack spacing={1}>
              <LinearProgress variant="determinate"
                              value={bulk.rows.length ? (100 * bulk.index) / bulk.rows.length : 100} />
              <Typography variant="body2">
                {bulk.running
                  ? `Working… ${bulk.index} of ${bulk.rows.length}`
                  : `${bulk.stopped ? "Stopped" : "Finished"}: ${bulk.index - bulk.failures.length} done, `
                    + `${bulk.failures.length} failed${bulk.stopped ? `, ${bulk.rows.length - bulk.index} not tried` : ""}.`}
              </Typography>
              {bulk.failures.map(f => (
                <Typography key={f.row.key} variant="caption" color="error">
                  {f.row.name || f.row.email}: {f.error}
                </Typography>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {bulk?.running && (
            <Button onClick={() => { stopBulk.current = true; }}>Stop</Button>
          )}
          {bulk && !bulk.running && !bulk.finished && (
            <>
              <Button onClick={() => setBulk(null)}>Cancel</Button>
              <Button variant="contained" disabled={bulk.rows.length === 0} onClick={runBulk}>Start</Button>
            </>
          )}
          {bulk?.finished && <Button variant="contained" onClick={() => setBulk(null)}>Close</Button>}
        </DialogActions>
      </Dialog>

      <PersonnelDialog personId={editing} onClose={() => setEditing(null)} />
    </Stack>
  );
}
